import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import { Resolvers } from '../../types/Resolvers';
import { getDataFromResolver } from '../../services/resolvers';
import { ModeloVehiculo } from '../../types/vehicle.types';
import { SmartCapitalizePipe } from '../../utils/pipes/smart-capitalize.pipe';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { toSlug } from '../../utils/slugs/slug.utils';

@Component({
  selector: 'allas-baterias-modelos',
  imports: [
    MainLayout,
    FormsModule,
    CommonModule,
    RouterModule,
    SmartCapitalizePipe,
    MarginWrapper,
  ],
  template: `
    <allas-main-layout>
      <allas-margin-wrapper styleClass="bg-white fs-condensed">
        <!-- Breadcrumb simple -->
        <div class="flex items-center text-sm text-gray-600 mb-6 mt-12">
          <a
            [routerLink]="['/bateria/para']"
            class="hover:text-allasRed cursor-pointer"
          >
            Inicio
          </a>
          <svg
            class="w-4 h-4 mx-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
          <a
            [routerLink]="['/bateria/para']"
            class="hover:text-allasRed cursor-pointer"
          >
            {{ brandName | smartCapitalize }}
          </a>
        </div>

        <!-- Titulo Principal oculto pero indexable -->
        <h1 class="absolute left-[-9999px]">
          Batería para {{ brandName | smartCapitalize }}
        </h1>

        <!-- Título simple -->
        <h2 class="text-2xl font-bold mb-8">
          Batería para {{ brandName | smartCapitalize }}
        </h2>

        @if (isLoading()) {
        <div class="text-center py-12 mb-12">
          <div class="text-gray-400 mb-4">
            <svg
              class="mx-auto w-8 h-8 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <span class="text-xl font-semibold text-gray-900 mb-2">
            Cargando modelos de vehículos...
          </span>
          <p class="text-gray-600">
            Estamos cargando la información más actualizada de vehículos.
          </p>
        </div>
        } @else if (groupedModels.length > 0) {
        <!-- Lista agrupada alfabéticamente -->
        <div class="columns-1 md:columns-3 lg:columns-4 gap-8 space-y-8 mb-12">
          @for (group of groupedModels; track group.letter) {
          <div class="break-inside-avoid space-y-4">
            <h3
              class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2"
            >
              {{ group.letter }}
            </h3>
            <ul class="space-y-2">
              @for (model of group.models; track model) {
              <li>
                <a
                  [href]="
                    '/repuestos' +
                    '/bateria-para-carro-auto-o-camioneta' +
                    '/' +
                    toSlug(brandName) +
                    '/' +
                    toSlug(model)
                  "
                  class="text-gray-700 hover:text-allasRed cursor-pointer text-sm block py-1"
                >
                  Batería para {{ brandName | smartCapitalize }}
                  {{ model | smartCapitalize }}
                </a>
              </li>
              }
            </ul>
          </div>
          }
        </div>
        } @else {
        <div class="mb-12">
          <p class="text-gray-600 mb-4">
            Próximamente modelos de {{ brandName | smartCapitalize }}
          </p>
          <a
            (click)="goToContact()"
            class="text-allasRed hover:underline cursor-pointer"
          >
            Contactar para consulta
          </a>
        </div>
        }
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
})
export class BateriasModelos implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private meta = inject(Meta);
  brandName: string = '';
  brandSlug: string = '';
  isLoading = signal(true);
  vehicleModels: ModeloVehiculo[] = [];
  groupedModels: Array<{ letter: string; models: string[] }> = [];
  toSlug = toSlug;

  constructor() {}

  ngOnInit() {
    this.setMetaTags();
    this.route.params.subscribe((params) => {
      this.brandSlug = params['brand'];
      this.loadBrandData();
    });
  }

  private async loadBrandData() {
    try {
      // Cargar datos del resolver
      const data = getDataFromResolver<ModeloVehiculo[]>(
        Resolvers.ALL_VEHICLE_MODELS,
        this.route
      );

      if (!data) {
        console.error('No se encontraron datos de modelos de vehículos');
        this.router.navigate(['/bateria/para']);
        return;
      }

      this.vehicleModels = data;

      // Buscar la marca por slug
      const marcasUnicas = [
        ...new Set(this.vehicleModels.map((item) => item.marca)),
      ];
      const marcaEncontrada = marcasUnicas.find(
        (marca) =>
          marca
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '') === this.brandSlug
      );

      if (marcaEncontrada) {
        this.brandName = marcaEncontrada;
        // Obtener todos los modelos de esta marca
        const models = this.vehicleModels
          .filter((item) => item.marca === marcaEncontrada)
          .map((item) => item.modelo)
          .sort();

        // Agrupar modelos alfabéticamente
        this.groupModelsAlphabetically(models);
      } else {
        // Si no se encuentra la marca, redirigir o mostrar error
        console.error('Marca no encontrada:', this.brandSlug);
        this.router.navigate(['/bateria/para']);
      }
    } catch (error) {
      console.error('Error cargando datos de modelos:', error);
      this.router.navigate(['/bateria/para']);
    } finally {
      this.isLoading.set(false);
    }
  }

  private groupModelsAlphabetically(models: string[]) {
    const groups: { [key: string]: string[] } = {};

    models.forEach((model) => {
      const firstLetter = model.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(model);
    });

    // Convertir a array y ordenar por letra
    this.groupedModels = Object.keys(groups)
      .sort()
      .map((letter) => ({
        letter,
        models: groups[letter].sort(),
      }));
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  goToContact(): void {
    // Implementar navegación a contacto
  }

  private setMetaTags() {
    const route = this.router.url;
    const pageTitle = 'Batería para tu vehículo';
    const description =
      'Encontrá la batería perfecta para tu vehículo, de las mejores marcas y a los mejores precios.';
    const keywords =
      'baterías, repuestos, vehículos, instalación, envío a domicilio, Honduras, Allas Repuestos, garantía, calidad, bateria para ford escape, bateria para toyota corolla, bateria para honda crv, bateria para nissan sentra, bateria para toyota hilux, bateria para mitsubishi l200';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });
    this.meta.updateTag({ property: 'og:url', content: route });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }
}
